<template>
  <div class="economy-tab">
    <div v-if="isLoading" class="economy-tab__loader">
      <b-spinner type="grow" size="lg" />
    </div>

    <div v-else-if="error" class="economy-tab__error">
      <p>{{ error }}</p>
      <BaseButton variant="primary" @click="fetchInvoices"
        >Prøv igjen</BaseButton
      >
    </div>

    <div v-else-if="!hasValidBookingNumber" class="economy-tab__empty">
      <p>Kan ikke vise fakturaer: Mangler gyldig bookingnummer</p>
    </div>

    <div v-else-if="!invoices.length" class="economy-tab__empty">
      <p>Ingen fakturaer funnet for denne avtalen</p>
    </div>

    <base-list v-else>
      <invoice-list-item
        v-for="invoice in sortedInvoices"
        :key="invoice.invoiceNumber"
        :invoice="mapToInvoiceDTO(invoice)"
        :list-item-id="'invoice-' + invoice.invoiceNumber"
        :project-supplier-name="tender.customerOrganization.orgNumber"
        :project-customer-name="tender.customerOrganization.orgNumber"
        :customer-mode="false"
      />
    </base-list>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from "vue";
import type { InvoiceDTO } from "@/custom-types/GeneralTypes";
import { useFinanceApi } from "@/services/api/useFinanceApi";
import { BaseList, InvoiceListItem } from "@really/really-tender-components";
import BaseButton from "@/components/Base/BaseButton/BaseButton.vue";
import dayjs from "dayjs";

const props = defineProps<{
  tender: {
    tenderId: string;
    customerOrganization: {
      orgNumber: string;
    };
  };
  bookingNumber: string | null;
  supplierId: number;
}>();

const invoices = ref<InvoiceDTO[]>([]);
const isLoading = ref(false);
const error = ref<string | null>(null);
const { getTenderInvoices } = useFinanceApi();

const hasValidBookingNumber = computed(() => {
  return !!props.bookingNumber && props.bookingNumber.trim() !== "";
});

const mapToInvoiceDTO = (invoice: InvoiceDTO): InvoiceDTO => ({
  id: invoice.id,
  invoiceNumber: invoice.invoiceNumber,
  customerName: invoice.customerName,
  invoiceDate: invoice.invoiceDate,
  paymentDueDate: invoice.paymentDueDate,
  taxInclusiveAmount: invoice.taxInclusiveAmount,
  statusCode: invoice.statusCode,
  restAmount: invoice.restAmount,
  vatTotalsAmount: invoice.vatTotalsAmount,
  taxExclusiveAmount: invoice.taxExclusiveAmount,
  emailAddress: invoice.emailAddress,
  comment: invoice.comment,
  credited: invoice.credited,
  lastPaymentDate: invoice.lastPaymentDate,
  deliveryDate: invoice.deliveryDate,
  ourReference: invoice.ourReference,
  yourReference: invoice.yourReference,
});

const sortedInvoices = computed(() => {
  return [...invoices.value].sort(
    (a, b) => dayjs(b.invoiceDate).valueOf() - dayjs(a.invoiceDate).valueOf(),
  );
});

const fetchInvoices = async () => {
  // Early return if no valid booking number
  if (!hasValidBookingNumber.value) {
    isLoading.value = false;
    return;
  }

  try {
    isLoading.value = true;
    error.value = null;

    const response = await getTenderInvoices({
      supplierId: props.supplierId,
      // Type assertion here since we've already checked validity
      bookingNumber: props.bookingNumber as string,
    });

    invoices.value = response.data;
  } catch (err) {
    error.value =
      "Det oppstod en feil ved henting av fakturaer. Vennligst prøv igjen.";
    console.error("Failed to fetch invoices:", err);
  } finally {
    isLoading.value = false;
  }
};

// Watch for changes in bookingNumber and refetch when it becomes valid
watch(
  () => props.bookingNumber,
  (newBookingNumber) => {
    if (newBookingNumber && newBookingNumber.trim() !== "") {
      fetchInvoices();
    } else {
      // Reset state when booking number becomes invalid
      invoices.value = [];
      error.value = null;
    }
  },
);

onMounted(() => {
  // Since the component will only be mounted when the tab is active,
  // we can fetch invoices directly in onMounted if we have a valid booking number
  if (hasValidBookingNumber.value) {
    fetchInvoices();
  }
});
</script>

<style lang="scss" scoped>
.economy-tab {
  padding: 1.5rem;

  &__loader,
  &__error,
  &__empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 200px;
    gap: 1rem;
    color: #1d1d1d;
    font-weight: 400;
  }
}
</style>
